import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Zap,
  BarChart3,
  FlaskConical,
  Mail,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { billingApi } from '../../../api/client';
import type { BillingEntitlements } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function planLabel(plan: string): string {
  return { free: 'Free', premium: 'Premium', elite: 'Elite' }[plan] ?? plan;
}

const PLAN_COLORS: Record<string, string> = {
  free:    'text-gray-400 bg-gray-400/10 border-gray-400/20',
  premium: 'text-brand bg-brand/10 border-brand/20',
  elite:   'text-purple-400 bg-purple-400/10 border-purple-400/20',
};

const PLAN_TIERS = ['free', 'premium', 'elite'] as const;

const PLAN_DESCRIPTIONS: Record<string, string> = {
  free:    'All features included — Analytics, Simulator, Backtest, and more',
  premium: 'Advanced tooling and priority access (coming soon)',
  elite:   'Full power suite for serious users (coming soon)',
};

const PLAN_PRICES: Record<string, Record<'month' | 'year', string>> = {
  free:    { month: 'Free', year: 'Free' },
  premium: { month: 'TBD',  year: 'TBD' },
  elite:   { month: 'TBD',  year: 'TBD' },
};

const CAPABILITY_LABELS: Record<string, string> = {
  analytics:   'Analytics',
  simulator:   'Strategy Simulator',
  backtesting: 'Backtesting',
};

const PLAN_MATRIX: Record<string, Record<string, boolean>> = {
  free:    { analytics: true, simulator: true, backtesting: true },
  premium: { analytics: true, simulator: true, backtesting: true },
  elite:   { analytics: true, simulator: true, backtesting: true },
};

function PlanIcon({ plan }: { plan: string }) {
  if (plan === 'elite') return <FlaskConical className="w-5 h-5" />;
  if (plan === 'premium') return <BarChart3 className="w-5 h-5" />;
  return <Zap className="w-5 h-5" />;
}

const YEARLY_DISCOUNT_TEXT = 'Save 17%';

// ── Main component ─────────────────────────────────────────────────────────────

export function SubscriptionSettingsPage() {
  const { can, hasRole } = usePermissions();

  const canView   = can('billing.view');
  const canManage = can('billing.manage') && hasRole(['owner']);

  const [entitlements,  setEntitlements]  = useState<BillingEntitlements | null>(null);
  const [isLoading,     setIsLoading]     = useState(true);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [error,         setError]         = useState<string | null>(null);
  const [successMsg,    setSuccessMsg]    = useState<string | null>(null);
  const [interval,      setIntervalVal]   = useState<'month' | 'year'>('month');

  useEffect(() => {
    if (!canView) return;
    setIsLoading(true);
    billingApi.getEntitlements()
      .then(setEntitlements)
      .catch((err) => setError((err as Error).message ?? 'Failed to load subscription data'))
      .finally(() => setIsLoading(false));
  }, [canView]);

  async function handleUpgrade(plan: string) {
    setLoadingAction(`checkout-${plan}`);
    setError(null);
    try {
      const { url } = await billingApi.createCheckout(plan, interval);
      if (url) window.location.href = url;
    } catch (err) {
      setError((err as Error).message ?? 'Failed to start checkout');
    } finally {
      setLoadingAction(null);
    }
  }

  if (!canView) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <p className="text-sm text-gray-500">You don't have permission to view subscription details.</p>
      </div>
    );
  }

  const currentPlan = entitlements?.plan ?? 'free';

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Subscription</h2>
        <p className="text-sm text-gray-500 mt-1">
          View your current plan, compare features, and manage upgrades.
        </p>
      </div>

      {/* Feedback banners */}
      {successMsg && (
        <div className="flex items-center gap-3 p-3 bg-win/10 border border-win/20 rounded-lg">
          <CheckCircle className="w-4 h-4 text-win flex-shrink-0" />
          <p className="text-sm text-win">{successMsg}</p>
          <button onClick={() => setSuccessMsg(null)} className="ml-auto text-win/60 hover:text-win" aria-label="Dismiss">×</button>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading subscription…
        </div>
      ) : (
        <>
          {/* Billing interval toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIntervalVal('month')}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                interval === 'month'
                  ? 'bg-brand/15 text-brand border border-brand/30'
                  : 'text-gray-400 border border-surface-4 hover:text-gray-200',
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setIntervalVal('year')}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                interval === 'year'
                  ? 'bg-brand/15 text-brand border border-brand/30'
                  : 'text-gray-400 border border-surface-4 hover:text-gray-200',
              )}
            >
              Yearly
              <span className="ml-1 text-win">{YEARLY_DISCOUNT_TEXT}</span>
            </button>
          </div>

          {/* Plan cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {PLAN_TIERS.map((plan) => {
              const isCurrent = plan === currentPlan;
              const isPaid    = plan !== 'free';

              return (
                <div
                  key={plan}
                  className={clsx(
                    'bg-surface-1 border rounded-lg p-4 flex flex-col gap-3 transition-shadow',
                    isCurrent
                      ? 'border-brand/50 ring-2 ring-brand/50 ring-offset-1 ring-offset-surface-0'
                      : isPaid
                        ? 'border-surface-4'
                        : 'border-surface-4',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <div className={clsx('p-1.5 rounded-md border', PLAN_COLORS[plan])}>
                      <PlanIcon plan={plan} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-100">{planLabel(plan)}</div>
                    </div>
                    {isCurrent && (
                      <span className="text-xs text-brand bg-brand/10 border border-brand/20 px-1.5 py-0.5 rounded flex-shrink-0">
                        Current
                      </span>
                    )}
                    {isPaid && (
                      <span className="text-xs text-gray-500 bg-surface-3 border border-surface-4 px-1.5 py-0.5 rounded flex-shrink-0">
                        Coming Soon
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 leading-relaxed">{PLAN_DESCRIPTIONS[plan]}</p>
                  <div className="text-xl font-bold text-gray-100">{PLAN_PRICES[plan][interval]}</div>

                  {canManage && !isCurrent && !isPaid && (
                    <button
                      onClick={() => handleUpgrade(plan)}
                      disabled={!!loadingAction}
                      className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-brand/10 text-brand border border-brand/30 rounded-md hover:bg-brand/20 transition-colors disabled:opacity-50"
                    >
                      {loadingAction === `checkout-${plan}` ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <Zap className="w-3 h-3" />
                      )}
                      Upgrade
                    </button>
                  )}

                  {isPaid && (
                    <Link
                      to="/access"
                      className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-light bg-brand/10 border border-brand/25 rounded-md hover:bg-brand/20 transition-colors"
                    >
                      <Mail className="w-3 h-3" />
                      Join Waitlist
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          {/* Feature matrix — always visible */}
          <div className="bg-surface-1 border border-surface-4 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-surface-4 bg-surface-2/50">
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Feature Comparison</span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-surface-4">
                  <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Feature</th>
                  {PLAN_TIERS.map((p) => (
                    <th
                      key={p}
                      className={clsx(
                        'text-center px-3 py-2.5 font-medium',
                        p === currentPlan ? 'text-brand' : 'text-gray-400',
                      )}
                    >
                      {planLabel(p)}
                      {p === currentPlan && <span className="ml-1 text-[9px] text-brand/60">✓</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(CAPABILITY_LABELS).map(([key, label]) => (
                  <tr key={key} className="border-b border-surface-4/50 last:border-0">
                    <td className="px-4 py-2 text-gray-400">{label}</td>
                    {PLAN_TIERS.map((p) => (
                      <td key={p} className="px-3 py-2 text-center">
                        {PLAN_MATRIX[p][key] ? (
                          <CheckCircle className="w-4 h-4 text-win mx-auto" />
                        ) : (
                          <XCircle className="w-4 h-4 text-gray-600 mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Entitlement summary */}
          {entitlements && (
            <div className="op-panel p-4 space-y-3">
              <h3 className="text-sm font-medium text-gray-300">Active Entitlements</h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                {Object.entries(entitlements.capabilities ?? {}).map(([key, enabled]) => (
                  <div key={key} className="flex items-center gap-2">
                    {enabled ? (
                      <CheckCircle className="w-3.5 h-3.5 text-win flex-shrink-0" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                    )}
                    <span className={clsx('text-xs capitalize', enabled ? 'text-gray-300' : 'text-gray-600')}>
                      {key.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </dl>
              {entitlements.renewsAt && (
                <p className="text-xs text-gray-600 pt-1 border-t border-surface-4">
                  Renews {formatDate(entitlements.renewsAt)}
                </p>
              )}
            </div>
          )}

          {!canManage && (
            <p className="text-xs text-gray-600 text-center">
              Only the organization owner can manage subscriptions.
            </p>
          )}
        </>
      )}
    </div>
  );
}
